import { useContext, useEffect, useRef } from 'react';
import segment from '@services/segment';
import { analytics } from '@services/analytics';
import {
  StylingType,
  ScoreboardPosition,
  ScoreboardType,
  PlayerCardPosition,
} from '@hooks/useOverlaySettings';
import { TeamContext } from '@services/Session';
import { getIsFlippedFromSettings } from '@helpers/utils/teamUtils';

interface StreamStylingTrackingProps {
  stylingType: StylingType;
  position: ScoreboardPosition;
  flipped: boolean;
}

export const useStreamStylingTracking = ({
  stylingType,
  position,
  flipped,
}: StreamStylingTrackingProps) => {
  const { id: teamId, sport, fetchOverlaySettings } = useContext(TeamContext);

  const initial = useRef<{
    preset: StylingType;
    position: ScoreboardPosition;
    flipped: boolean;
  } | null>(null);

  const current = useRef<{
    preset: StylingType;
    position: ScoreboardPosition;
    flipped: boolean;
  }>({ preset: stylingType, position, flipped });

  useEffect(() => {
    current.current = {
      preset: stylingType,
      position,
      flipped,
    };
  }, [stylingType, position, flipped]);

  useEffect(() => {
    if (!teamId || !fetchOverlaySettings || initial.current) {
      return;
    }

    const { scoreboardType, scoreboardPosition, playerCardPosition } =
      fetchOverlaySettings;

    const preset: StylingType =
      scoreboardType === 'default'
        ? 'default'
        : scoreboardType === 'minimal'
          ? 'minimal'
          : 'other';

    const flippedInit = getIsFlippedFromSettings(
      scoreboardPosition as ScoreboardPosition,
      playerCardPosition as PlayerCardPosition,
      scoreboardType as ScoreboardType
    );

    initial.current = {
      preset,
      position: scoreboardPosition as ScoreboardPosition,
      flipped: flippedInit,
    };

    analytics.track('Stream Styling Session Started', {
      sport,
      teamId,
      initialPreset: preset,
      initialScoreboardPosition: scoreboardPosition,
      initialPlayerCardFlipped: flippedInit,
    });

    segment.track('Stream Styling Session Started', {
      sport,
      teamId,
      initialPreset: preset,
      initialScoreboardPosition: scoreboardPosition as ScoreboardPosition,
      initialPlayerCardFlipped: flippedInit,
    });
  }, [teamId, sport, fetchOverlaySettings]);

  useEffect(() => {
    return () => {
      if (!teamId || !sport || !initial.current) {
        return;
      }

      const {
        preset: initialPreset,
        position: initialScoreboardPosition,
        flipped: initialPlayerCardFlipped,
      } = initial.current;

      const {
        preset: finalPreset,
        position: finalScoreboardPosition,
        flipped: finalPlayerCardFlipped,
      } = current.current;

      const props = {
        sport,
        teamId,
        initialPreset,
        initialScoreboardPosition,
        initialPlayerCardFlipped,
        finalPreset,
        finalScoreboardPosition,
        finalPlayerCardFlipped,
        presetChanged: finalPreset !== initialPreset,
        scoreboardChanged:
          finalScoreboardPosition !== initialScoreboardPosition,
        cardFlipChanged: finalPlayerCardFlipped !== initialPlayerCardFlipped,
        presetTransition: `${initialPreset}→${finalPreset}`,
        scoreboardTransition: `${initialScoreboardPosition}→${finalScoreboardPosition}`,
      };

      analytics.track('Stream Styling Session Ended', props);
      segment.track('Stream Styling Session Ended', props);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
